import HttpClient from 'utils/HttpClient';
import ODataUtil from './ODataUtil';
const baseApiUrl = "/services/api";

/**
 * Inherited by all OData based classes
 * 
 * contains a common framework and method set to use odata queries and expands, and flatten them.
 * 
 */
class BaseResource {
  constructor(args, attributes) {
    let reducedExpand, tree;
    if (args && args.expand) {
      reducedExpand = ODataUtil.buildExpandTree(args.expand);
    };

    // attributes.forEach((item) => {
    //   Object.defineProperty(this, item, {
    //     get() {
    //       return this[`_${item}`];
    //     },
    //     set(value) {
    //       this[`_${item}`] = value;
    //     }
    //   });
    // })

    attributes.forEach((item, index) => {
      let mapped = false;
      if (args) {
        if(args && args.collectionFilterAttributes && args.collectionFilterAttributes.length > 0 && item && args.collectionFilterAttributes.find(r => r.field === item)) {
          const collectionAtts = args.collectionFilterAttributes.find(r => r.field === item);
          this[item] = collectionAtts.mapToAttributes(args);
          mapped = true;
        }
        else if (args && args.expand && item && item.split("_")[1]) {
          const [split0, split1] = item.split("_");
          try {
            tree = reducedExpand[split1].tree;
          } catch(e) {
            throw Error(`Error retrieving Expanded OData reference. Ensure all expanded fields in the class follow {Field}_{table} scheme. this error refers to field '${item}'`)
          };
          if (reducedExpand && (split1 in reducedExpand)) {
            if (tree) {
              let lookup = args;
              const tables = tree.includes("/") ? tree.split("/") : [tree];
              tables.forEach((table, index) => {
                if (index === 0) { table = table.substr(0, 1).toLowerCase() + table.slice(1) };
                if (lookup && table in lookup) { lookup = lookup[table]; } else { lookup = null; };
              });
              if (lookup) { 
                this[item] = lookup[split0];
                mapped = true;
              };
            }
          }
        } else {
          if(args.hasOwnProperty(item)){
            this[item] = args[item];
            mapped = true;
          } else {
            const asCamelCase = item.charAt(0).toLowerCase() + item.slice(1);
            if(args.hasOwnProperty(asCamelCase)) {
              this[item] = args[asCamelCase]
            }
            mapped = true;
          }
        };
      };

      if(!mapped) {
        const className = this.constructor.name;
        console.warn(`An instance of class '${className}' is unable to map attribute '${item}' to a value`);
      }
    });
  };

  static columns = []
  static attributes = []

  static convertToTableRows = function convertToTableRows({ "value": items, "@odata.count": count }) {
    let data = items && items.map(r => {
      const resource = new this({
        ...Object.fromEntries(
          Object.entries(r).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v])
        ),
        expand: this.expand,
        collectionFilterAttributes: this.collectionFilterAttributes ? this.collectionFilterAttributes : []
      });
      return resource;
    });

    if (data === undefined) {
        data = []
    }
    return {
      data, count
    };
  };

  static expand = [];

  static collectionFilterAttributes = [];

  static constructWithExpand(args) {
    return new this({
      ...Object.fromEntries(
        Object.entries(args).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v])
      ),
      expand: this.expand,
      collectionFilterAttributes: this.collectionFilterAttributes ? this.collectionFilterAttributes : []
    });
  }

}

/**
 * This class wraps all API responses in a wrapper and assigns them a success attribute that
 * is true of false
 */
class ApiResponse {
  constructor(args, success) {
    if (!Array.isArray(args)) {
      Object.keys(args).forEach((attribute, index) => {
        this[attribute] = args[attribute];
      });
    }
    else {
      this.data = args;
    }

    this.success = success;
  };
};

const ApiRequest = async (resource_path, url_params = '', opts, contentType = {}, blob = false) => {
  try {
    const req = await HttpClient(`${baseApiUrl}${resource_path}${url_params}`, opts, contentType);
    if (!blob) {
      if (req.status === 200) {
        let data;
        try {
          data = await req.json();
        } catch(e) {
          try {
            const text = await req.text();
            data = { data: text }
          } catch(e) {
            data = {}
          }
        };
        return new ApiResponse(data, true);
      } else {
        const t = await req.text();
        throw Error(t);
      }
    }
    else {
      const blobObj = await req.blob()
      if (req.status === 200) {
        return blobObj
      }
    }
  } catch (e) {
    return new ApiResponse({ details: e }, false);
  };
};

const createFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((item, index) => {
    formData.append(item, data[item]);
  });
  return formData;
}

const postItem = async (endpoint, url_params, data, uploadFile = false, arrayObject = false) => {

  let formData
  let contentType = {}

  if (uploadFile)
    formData = data;
  else if (arrayObject) {
    formData = JSON.stringify(data)
    contentType = { 'Content-Type': 'application/json' }
  }
  else
    formData = createFormData(data);
  const opts = {
    method: "POST",
    body: formData,
  };
  const payload = await ApiRequest(endpoint, url_params, opts, contentType);
  return payload;
};

const deleteItem = async (endpoint, url_params, data) => {
  const opts = {
    method: "DELETE"
  };

  let payload;

  if (data) {
    opts.body = JSON.stringify(data);
    const contentType = { 'Content-Type': 'application/json' };
    payload = await ApiRequest(endpoint, url_params, opts, contentType);
  } else {
    payload = await ApiRequest(endpoint, url_params, opts);
  }

  return payload;
};

const putItem = async (endpoint, url_params, data, arrayObject = false) => {
  let formData
  let contentType = {}

  if (arrayObject) {
    formData = JSON.stringify(data)
    contentType = { 'Content-Type': 'application/json' }
  }
  else
    formData = createFormData(data);

  const opts = {
    method: "PUT",
    body: formData
  };
  const payload = await ApiRequest(endpoint, url_params, opts, contentType);
  return payload;
};


const patchItem = async (endpoint, url_params, data) => {

  const payload = [];
  for (const [key, val] of Object.entries(data)) {
    payload.push({
      op: "replace",
      path: `/${key}`,
      value: val
    })
  }

  // Use the JSON Patch media type, not application/json. After the OData upgrade the OData
  // input formatter claims application/json on these /api routes and can't bind
  // JsonPatchDocument<T> (→ 415). application/json-patch+json routes straight to the
  // Newtonsoft JsonPatch formatter, which is what these PATCH actions bind from.
  const contentType = { 'Content-Type': 'application/json-patch+json' }

  const opts = {
    method: "PATCH",
    body: JSON.stringify(payload)
  };
  const req = await ApiRequest(endpoint, url_params, opts, contentType);
  return req;
};

export { baseApiUrl, ApiRequest, BaseResource, postItem, putItem, patchItem, deleteItem };